import { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch, apiImageUrl } from '../../utils/streamApi';
import IndustrialLayout from './IndustrialLayout';
import ReportDataMenu from '../../components/ReportDataMenu';

// ── Vehicle Report ────────────────────────────────────────────────────────────
// Vehicles per day, and each vehicle's number plate when it could be read, for
// counting lines configured with purpose='vehicle' on the Object Intelligence page.
// Same detector and event log as the other count reports; vehicle lines add the
// vehicle type and a plate read (text + status + plate photo) to each crossing.
//
// Plate status, as the backend reports it:
//   read    the text fits the plate format and the OCR was confident
//   unsure  a plate was found but the text is doubtful — shown as a guess
//   small   a plate was found but too small to read
//   none    no plate seen (rear-on, occluded, or washed out at night)
// The plate photo is kept for every status but none, so a person can still look.

const PAGE = 100;
const isoDay = (d) => {
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const dayOf = (ts) => isoDay(new Date(ts * 1000));
const TYPE_ICON = { car: '🚗', truck: '🚚', bus: '🚌', motorcycle: '🏍️', bicycle: '🚲' };

export default function IndustrialVehicleReportPage() {
  const [cameras, setCameras] = useState([]);
  const [cam, setCam]         = useState('');          // '' = every vehicle camera
  const [from, setFrom]       = useState(() => { const d = new Date(); d.setDate(d.getDate() - 6); return isoDay(d); });
  const [to, setTo]           = useState(() => isoDay(new Date()));
  const [fromTime, setFromTime] = useState('');        // '' = from the start of the From day
  const [toTime, setToTime]     = useState('');        // '' = to the end of the To day
  const [plateQ, setPlateQ]   = useState('');
  const [data, setData]       = useState({ days: [], vehicles: [] });
  const [day, setDay]         = useState(null);        // selected day for the vehicle list
  const [onlyPlates, setOnlyPlates] = useState(false);
  const [page, setPage]       = useState(0);
  const [shot, setShot]       = useState(null);        // { e, plateUrl, snapUrl } lightbox

  const vehicleCams = useMemo(() => cameras.filter(c =>
    ((c.detector_config?.object_count?.lines) || []).some(L => L.purpose === 'vehicle' && L.line)
  ), [cameras]);

  useEffect(() => {
    apiFetch('/ui/cameras').then(r => r.json()).then(d => setCameras(d.cameras || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const p = [`date_from=${from}`, `date_to=${to}`, 'limit=20000'];
    if (fromTime) p.push(`ts_from=${Math.floor(new Date(`${from}T${fromTime}`).getTime() / 1000)}`);
    if (toTime)   p.push(`ts_to=${Math.floor(new Date(`${to}T${toTime}`).getTime() / 1000)}`);
    if (cam !== '') p.push(`cam=${cam}`);
    if (plateQ.trim()) p.push(`plate=${encodeURIComponent(plateQ.trim())}`);
    apiFetch(`/ui/count/vehicles?${p.join('&')}`).then(r => r.json())
      .then(d => setData({ days: d.days || [], vehicles: d.vehicles || [], oldest: d.oldest, retentionDays: d.retention_days })).catch(() => {});
  }, [from, to, fromTime, toTime, cam, plateQ]);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  // Land on the newest day that has vehicles; keep the user's pick while it exists.
  useEffect(() => {
    if (!data.days.length) { setDay(null); return; }
    if (!day || !data.days.some(d => d.date === day)) setDay(data.days[0].date);
  }, [data.days, day]);
  useEffect(() => { setPage(0); }, [day, onlyPlates, cam, plateQ, fromTime, toTime]);

  const totals = useMemo(() => data.days.reduce((t, d) => ({
    total: t.total + d.total, read: t.read + d.plates_read,
  }), { total: 0, read: 0 }), [data.days]);

  const rows = useMemo(() => data.vehicles
    .filter(e => !day || dayOf(e.ts) === day)
    .filter(e => !onlyPlates || e.plate_status === 'read'), [data.vehicles, day, onlyPlates]);
  const shown = rows.slice(page * PAGE, (page + 1) * PAGE);
  const sel = data.days.find(d => d.date === day);

  // Every day still on record, oldest to today. Rows past the retention are gone from
  // the box, so the note under the table says how far back that is.
  const allDays = !!data.oldest && from <= data.oldest && to >= isoDay(new Date()) && !fromTime && !toTime;
  const showAllDays = () => { setFrom(data.oldest); setTo(isoDay(new Date())); setFromTime(''); setToTime(''); };

  const open = async (e) => {
    const get = (f) => f ? apiImageUrl(`/ui/count/snapshot/${encodeURIComponent(f)}`).catch(() => null) : Promise.resolve(null);
    // The vehicle photo (best full view while tracked) beats the crossing frame, where
    // the vehicle is often half out of the picture; older events only have the latter.
    const [plateUrl, snapUrl] = await Promise.all([get(e.plate_snapshot), get(e.vehicle_snapshot || e.snapshot)]);
    setShot({ e, plateUrl, snapUrl });
  };
  const close = () => {
    if (shot?.plateUrl) URL.revokeObjectURL(shot.plateUrl);
    if (shot?.snapUrl) URL.revokeObjectURL(shot.snapUrl);
    setShot(null);
  };

  const exportCsv = () => {
    const fmt = ts => new Date(ts * 1000).toLocaleString();
    const out = [['time', 'camera', 'line', 'direction', 'vehicle', 'plate', 'plate_status', 'plate_conf', 'night'],
      ...rows.map(e => [fmt(e.ts), e.cam_name, e.line, e.direction, e.vehicle, e.plate || '',
                        e.plate_status || '', e.plate_conf ?? '', e.night ? 'yes' : ''])];
    const csv = out.map(r => r.map(x => `"${String(x ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `vehicles_${day || from}.csv`; a.click();
  };

  if (cameras.length && vehicleCams.length === 0 && !data.vehicles.length) {
    return (
      <IndustrialLayout eyebrow="Vehicles" title="Vehicle Report" subtitle="NO VEHICLE LINES CONFIGURED">
        <div className="ind-card">
          <div className="ind-empty" style={{ padding: 28, lineHeight: 1.7 }}>
            <b>No vehicle counting is set up yet.</b><br />
            Open <b>Object Intelligence Config</b>, draw a counting line across the gate or lane,
            and set <b>“What is this line for?”</b> to <b>Vehicles in / out — with number plates</b>.<br />
            Plates are read from the camera’s main stream, so pick a camera that sees plates up close
            — a wide overview camera counts vehicles but rarely reads plates.
          </div>
        </div>
      </IndustrialLayout>
    );
  }

  return (
    <IndustrialLayout eyebrow="Vehicles" title="Vehicle Report"
      subtitle={`${totals.total} VEHICLES · ${totals.read} PLATES READ`}>

      <div className="ind-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <select value={cam} onChange={e => setCam(e.target.value)} style={selS}>
            <option value="">All vehicle cameras</option>
            {vehicleCams.map(c => <option key={c.idx} value={c.idx}>🎥 {c.name || `Camera ${c.idx}`}</option>)}
          </select>
          <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} style={selS} aria-label="From" />
          <input type="time" value={fromTime} onChange={e => setFromTime(e.target.value)} style={selS} aria-label="From time" />
          <span style={{ color: 'var(--ind-tx3)', fontSize: 12 }}>to</span>
          <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)} style={selS} aria-label="To" />
          <input type="time" value={toTime} onChange={e => setToTime(e.target.value)} style={selS} aria-label="To time" />
          {(fromTime || toTime) && <button className="ind-pill" onClick={() => { setFromTime(''); setToTime(''); }}>Clear time</button>}
          <button className={`ind-pill${allDays ? ' on' : ''}`} disabled={!data.oldest} onClick={showAllDays}>All days</button>
          <input value={plateQ} onChange={e => setPlateQ(e.target.value)} placeholder="Search plate…"
            style={{ ...selS, width: 140, fontFamily: 'var(--ind-mono)', textTransform: 'uppercase' }} />
          <span style={{ flex: 1 }} />
          {(() => {
            // Data is reset per camera — the one picked, or the only vehicle camera there is.
            const c = cam !== '' ? vehicleCams.find(x => String(x.idx) === String(cam))
                                 : (vehicleCams.length === 1 ? vehicleCams[0] : null);
            if (!c) return null;
            const its = ((c.detector_config?.object_count?.lines) || [])
              .filter(L => L.purpose === 'vehicle').map(L => L.name).filter(Boolean);
            return <ReportDataMenu kind="count" cam={c.idx} camName={c.name || `Camera ${c.idx}`}
              items={its} onChanged={load} />;
          })()}
        </div>

        {/* Per-day summary — click a day to list its vehicles below */}
        <span className="ind-eye">Vehicles per day</span>
        <div style={{ overflowX: 'auto', border: '1px solid var(--ind-line)', borderRadius: 8, marginTop: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 560 }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--ind-tx3)' }}>
              <th style={th}>Day</th><th style={th}>Vehicles</th><th style={th}>In</th><th style={th}>Out</th>
              <th style={th}>By type</th><th style={th}>Plates read</th><th style={th}>Unique plates</th>
              <th style={th}>Doubtful / none</th>
            </tr></thead>
            <tbody>
              {data.days.map(d => (
                <tr key={d.date} onClick={() => setDay(d.date)}
                  style={{ borderTop: '1px solid var(--ind-line)', cursor: 'pointer',
                           background: d.date === day ? 'var(--ind-ink)' : 'transparent' }}>
                  <td style={td}>{new Date(`${d.date}T00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{d.total}</td>
                  <td style={{ ...td, color: 'var(--ind-ok)' }}>{d.in}</td>
                  <td style={{ ...td, color: 'var(--ind-accent)' }}>{d.out}</td>
                  <td style={td}>{Object.entries(d.by_type).sort((a, b) => b[1] - a[1])
                    .map(([t, n]) => `${TYPE_ICON[t] || '🚘'} ${n}`).join('  ')}</td>
                  <td style={td}>{d.plates_read}<span style={{ color: 'var(--ind-tx3)' }}>{d.total ? ` (${Math.round(100 * d.plates_read / d.total)}%)` : ''}</span></td>
                  <td style={td}>{d.unique_plates}</td>
                  <td style={{ ...td, color: 'var(--ind-tx3)' }}>{d.plates_unsure} / {d.plates_none}</td>
                </tr>
              ))}
              {!data.days.length && <tr><td style={td} colSpan={8}>No vehicles in this range.</td></tr>}
            </tbody>
          </table>
        </div>
        {data.oldest && (
          <p style={{ fontSize: 11, color: 'var(--ind-tx3)', margin: '8px 0 0' }}>
            Oldest vehicle on record: {new Date(`${data.oldest}T00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
            {data.retentionDays ? ` · Crossings are kept for ${data.retentionDays} days` : ''}
          </p>
        )}
        {sel && sel.night > 0 && (
          <p style={{ fontSize: 11, color: 'var(--ind-tx3)', margin: '8px 0 0', lineHeight: 1.6 }}>
            {sel.night} of {sel.total} crossings on this day were in low light. Plates at night are often
            washed out by headlights or IR glare — those vehicles are still counted, and the plate photo is
            kept where one was found.
          </p>
        )}
      </div>

      <div className="ind-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <span className="ind-eye">{day ? new Date(`${day}T00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' }) : 'Vehicles'}</span>
          <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>{rows.length} vehicles</span>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, marginLeft: 8 }}>
            <input type="checkbox" checked={onlyPlates} onChange={e => setOnlyPlates(e.target.checked)} />
            Only with a plate read
          </label>
          <button className="ind-pill" style={{ marginLeft: 'auto' }} onClick={exportCsv} disabled={!rows.length}>⭳ CSV</button>
        </div>
        <div style={{ overflowX: 'auto', border: '1px solid var(--ind-line)', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 520 }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--ind-tx3)' }}>
              <th style={th}>Time</th><th style={th}>Plate</th><th style={th}>Vehicle</th>
              <th style={th}>Dir</th><th style={th}>Camera · line</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {shown.map(e => (
                <tr key={e.eid || `${e.ts}-${e.cam_idx}-${e.track_id}`} style={{ borderTop: '1px solid var(--ind-line)' }}>
                  <td style={td}>{new Date(e.ts * 1000).toLocaleTimeString()}{e.night ? ' 🌙' : ''}</td>
                  <td style={td}><Plate e={e} /></td>
                  <td style={{ ...td, fontFamily: 'inherit' }}>{TYPE_ICON[e.vehicle] || '🚘'} {e.vehicle}</td>
                  <td style={{ ...td, color: e.direction === 'in' ? 'var(--ind-ok)' : 'var(--ind-accent)' }}>{e.direction.toUpperCase()}</td>
                  <td style={{ ...td, fontFamily: 'inherit', color: 'var(--ind-tx2)' }}>{e.cam_name} · {e.line}</td>
                  <td style={td}>
                    {(e.plate_snapshot || e.vehicle_snapshot || e.snapshot) &&
                      <button className="ind-pill" style={{ padding: '2px 8px' }} onClick={() => open(e)}>📷</button>}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td style={td} colSpan={6}>No vehicles{onlyPlates ? ' with a plate read' : ''} {fromTime || toTime ? 'in this time window' : 'on this day'}.</td></tr>}
            </tbody>
          </table>
        </div>
        {rows.length > PAGE && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 8, fontSize: 12 }}>
            <button className="ind-pill" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Newer</button>
            <span style={{ color: 'var(--ind-tx3)' }}>{page * PAGE + 1}–{Math.min(rows.length, (page + 1) * PAGE)} of {rows.length}</span>
            <button className="ind-pill" disabled={(page + 1) * PAGE >= rows.length} onClick={() => setPage(p => p + 1)}>Older ›</button>
          </div>
        )}
      </div>

      {shot && (
        <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={ev => ev.stopPropagation()} style={{ background: 'var(--ind-ink)', borderRadius: 10,
            padding: 14, maxWidth: 'min(960px, 100%)', maxHeight: '100%', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <Plate e={shot.e} big />
              <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>
                {new Date(shot.e.ts * 1000).toLocaleString()} · {shot.e.cam_name} · {shot.e.direction.toUpperCase()}
                {shot.e.plate_conf != null && ` · OCR ${Math.round(shot.e.plate_conf * 100)}%`}
              </span>
              <button className="ind-pill" style={{ marginLeft: 'auto' }} onClick={close}>✕</button>
            </div>
            {shot.plateUrl && <img src={shot.plateUrl} alt="Number plate" style={{ display: 'block', maxWidth: '100%', maxHeight: 160, marginBottom: 10, borderRadius: 6 }} />}
            {shot.snapUrl && <img src={shot.snapUrl} alt="Vehicle" style={{ display: 'block', maxWidth: '100%', maxHeight: '70vh', margin: '0 auto', borderRadius: 6 }} />}
          </div>
        </div>
      )}
    </IndustrialLayout>
  );
}

function Plate({ e, big }) {
  const fs = big ? 18 : 13;
  if (e.plate_status === 'read' && e.plate)
    return <span style={{ fontFamily: 'var(--ind-mono)', fontWeight: 700, fontSize: fs, letterSpacing: '.06em',
      padding: '1px 6px', borderRadius: 4, border: '1px solid var(--ind-line)', color: 'var(--ind-tx)' }}>{e.plate}</span>;
  if (e.plate_status === 'unsure' && e.plate)
    return <span title="Plate found but the text is doubtful — check the photo"
      style={{ fontFamily: 'var(--ind-mono)', fontSize: fs, color: 'var(--ind-tx3)' }}>{e.plate}?</span>;
  if (e.plate_status === 'pending')
    return <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>reading…</span>;
  const why = e.plate_status === 'unsure' || e.plate_status === 'small' ? 'unreadable' : 'no plate';
  return <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>— {why}</span>;
}

const selS = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12 };
const th  = { padding: '6px 10px', fontWeight: 600, whiteSpace: 'nowrap' };
const td  = { padding: '6px 10px', fontFamily: 'var(--ind-mono)', whiteSpace: 'nowrap' };
